import { LogOut, Shield, Users } from "lucide-react";
import { Button } from "./ui/button.js";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card.js";
import {
  signOut,
  getIdToken,
  parseIdToken,
  getUserRole,
} from "../lib/auth.js";

// LoginPanel has been replaced by AuthShell (in-app sign-in/sign-up/forgot/
// reset forms). This file now only exports the two smaller pieces still
// referenced from App.tsx.

export function UnconfirmedPanel() {
  const token = getIdToken();
  const user = token ? parseIdToken(token) : null;

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 via-white to-slate-100 p-4">
      <Card className="w-full max-w-md border-amber-200 shadow-lg">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-amber-100">
            <Shield className="h-7 w-7 text-amber-600" />
          </div>
          <CardTitle className="text-xl font-bold text-slate-900">Awaiting Approval</CardTitle>
          <CardDescription className="text-slate-500">
            Your account is pending admin approval
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 pt-4">
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
            <p className="text-sm text-amber-800">
              Please contact an administrator for approval.
            </p>
            {user?.email && (
              <p className="mt-2 text-xs text-amber-700/80">
                Signed in as <strong>{user.email}</strong>.
              </p>
            )}
          </div>
          <Button onClick={signOut} variant="outline" className="h-11 w-full">
            <LogOut className="mr-2 h-4 w-4" />
            Sign out
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

export function SignOutButton({ onAdminClick }: { onAdminClick?: () => void }) {
  const token = getIdToken();
  const user = token ? parseIdToken(token) : null;
  const role = getUserRole();
  const showAdmin = role === "admin";

  return (
    <div className="flex items-center gap-2">
      {showAdmin && onAdminClick && (
        <button
          onClick={onAdminClick}
          className="flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-600 shadow-sm hover:text-slate-900 hover:border-slate-300"
          title="User Management"
        >
          <Users className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Admin</span>
        </button>
      )}
      <div className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 shadow-sm">
        <span
          className={`h-2 w-2 rounded-full ${
            role === "admin"
              ? "bg-blue-500"
              : role === "user"
                ? "bg-emerald-500"
                : "bg-amber-500"
          }`}
        />
        <span className="text-sm text-slate-700">{user?.email ?? "Signed in"}</span>
        <button
          onClick={signOut}
          className="ml-1 text-slate-400 hover:text-slate-600"
          title="Sign out (use Forgot password on the sign-in screen to change your password)"
        >
          <LogOut className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
